// eslint-disable-next-line import/no-cycle
import { getShopDefaultLang, UserState } from '@/composables/User';
// eslint-disable-next-line import/no-cycle
import {
  addSmartListOriginalCode, htmlHasSmartlist, restoreSmartListsOriginalCode, TemplateEditorState, togglePanel,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { getSmartListHtmlCode } from '@/components/template-builder/getters';
// eslint-disable-next-line import/no-cycle
import {
  applySpmCollections,
  TemplateContext,
} from '@/components/template-builder/utils/sanitizer';
// eslint-disable-next-line import/no-cycle
import { addWidgetsListeners } from '@/components/template-builder/utils/listeners';
// eslint-disable-next-line import/no-cycle
import { Maybe } from '@/types/generated-types/graphql';
// eslint-disable-next-line import/no-cycle
import { TemplateStructureEnum } from '@/types';
// eslint-disable-next-line import/no-cycle
import { addItemElementDecorations } from '@/components/template-builder/utils/active-item-builder';
// eslint-disable-next-line import/no-cycle
import { store } from '@/store';
// eslint-disable-next-line import/no-cycle
import { getFirstParent } from '@/helpers';
// eslint-disable-next-line import/no-cycle
import { getTemplateWidgets } from '../utils/parser';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { updateIframeTranslations } from '../utils/translate';
// eslint-disable-next-line import/no-cycle
import { refreshButtonVml } from '@/components/template-builder/setters';

export const restoreVoucherButtons = () => {
  const template = getTemplateIframeDocument();
  if (!template) return;

  // Find all voucher widgets in the iframe and restore their button state
  const allButtons = template.querySelectorAll('.spm_container_voucher_button');
  allButtons.forEach((button: Element) => {
    (button as HTMLElement).classList.remove('spm_hide_on_builder');
  });

  const allCodes = template.querySelectorAll('.spm_container_voucher_code');
  allCodes.forEach((code: Element) => {
    (code as HTMLElement).classList.add('spm_hidden');
    const children: Array<HTMLElement> = Array.from(code.querySelectorAll('*'));
    children.forEach((child: HTMLElement) => child.classList.add('spm_hidden'));
  });
};

export const showForm = (selector: string) => {
  const template = getTemplateIframeDocument();
  const container: HTMLElement|null = template?.querySelector(selector);
  const form: HTMLElement|null = container?.querySelector('.spm_step1') as HTMLElement;
  if (form) {
    form.classList.remove('spm_hidden');
  }
  const message: HTMLElement|null = container?.querySelector('.spm_step2') as HTMLElement;
  if (message) {
    message.classList.add('spm_hidden');
  }
};

export const switchVoucherButtonsAndCodes = () => {
  const selectedStructure = store.getters['liveEditor/getSelectedStructure'];
  const template = getTemplateIframeDocument();
  if (!template) return;

  const selector = selectedStructure?.identifier ? `#${selectedStructure.identifier}` : '';

  const buttons = template.querySelectorAll(`${selector} .spm_container_voucher_button`);
  buttons.forEach((button: Element) => {
    (button as HTMLElement).classList.toggle('spm_hide_on_builder');
  });

  const codes = template.querySelectorAll(`${selector} .spm_container_voucher_code`);
  codes.forEach((code: Element) => {
    (code as HTMLElement).classList.toggle('spm_hidden');
    const children: Array<HTMLElement> = Array.from(code.querySelectorAll('.spm_hidden'));
    children.forEach((child: HTMLElement) => child.classList.toggle('spm_hidden'));
  });
};

export const switchFormAndMessage = (selector: string) => {
  const template = getTemplateIframeDocument();
  const container: HTMLElement|null = template?.querySelector(selector);
  const form: HTMLElement|null = container?.querySelector('.spm_step1') as HTMLElement;
  if (form) {
    form.classList.toggle('spm_hidden');
  }
  const message: HTMLElement|null = container?.querySelector('.spm_step2') as HTMLElement;
  if (message) {
    message.classList.toggle('spm_hidden');
  }
};

/**
 * Toggle error class on form fields
 * @param addError - true to add error class, false to remove it
 */
export const toggleFormFieldsError = (addError: boolean) => {
  const iframeDoc = getTemplateIframeDocument();
  if (!iframeDoc) return;

  if (addError) {
    const formFields = iframeDoc.querySelectorAll(
      '.spm_form_field input, .spm_form_field textarea, .spm_form_field .spm_custom_select__trigger',
    );
    formFields.forEach((field) => {
      field.classList.add('spm_error');
    });
  } else {
    const errorFields = iframeDoc.querySelectorAll('.spm_error');
    errorFields.forEach((field) => {
      field.classList.remove('spm_error');
    });
  }
};

const refreshSmartProductListPending: Record<string, boolean> = {};
const refreshSmartProductListTimeouts: any = {};
// Cache of last rendered state per widget: "stateHtml|lang"
const lastRenderedSmartListState: Record<string, string> = {};
// Track the smartLists proxy identity to detect template changes (SPA navigation).
// When a new Template instance is created, state.template.smartLists is a new object,
// so the Vue readonly proxy identity changes — we use this to clear stale render cache.
let lastSmartListsRef: unknown = null;

/**
 * Clear the smart list render cache so the next refreshSmartProductList() performs a full render.
 * Called by restoreSmartListsOriginalCode() to keep DOM and cache in sync.
 */
export const clearSmartListRenderCache = (): void => {
  Object.keys(lastRenderedSmartListState).forEach((k) => delete lastRenderedSmartListState[k]);
};

/**
 * Build a cache key from the current smart list state and language.
 * Returns null if no smart list widgets are found.
 */
const buildSmartListCacheKey = (widgetToRefresh: Maybe<string>, lang: string): string | null => {
  const smartLists = TemplateEditorState.template?.smartLists;
  if (!smartLists) return null;

  if (widgetToRefresh) {
    const html = smartLists[widgetToRefresh];
    return html ? `${html}|${lang}` : null;
  }

  // For "refresh all", combine all widget states
  const keys = Object.keys(smartLists).sort();
  if (!keys.length) return null;
  return keys.map((k) => smartLists[k]).join('||') + `|${lang}`;
};

export const refreshSmartProductList = async (widgetToRefresh: Maybe<string> = null, lang: Maybe<string> = null) => {
  const shopDefaultLanguage = getShopDefaultLang();
  const refreshSmartProductListTimeoutKey: string = (!widgetToRefresh ? 'all' : widgetToRefresh);
  if (refreshSmartProductListTimeouts[refreshSmartProductListTimeoutKey]) {
    window.clearTimeout(refreshSmartProductListTimeouts[refreshSmartProductListTimeoutKey]);
  }
  refreshSmartProductListTimeouts[refreshSmartProductListTimeoutKey] = window.setTimeout(async () => {
    if (!widgetToRefresh || !Object.keys(refreshSmartProductListPending).includes(widgetToRefresh)) {
      if (widgetToRefresh) {
        refreshSmartProductListPending[widgetToRefresh] = true;
      }

      const effectiveLang = lang ?? shopDefaultLanguage;

      // Detect new template session (SPA navigation / template reload).
      // When a new Template is created, smartLists is a fresh object → different proxy.
      const currentSmartListsRef = TemplateEditorState.template?.smartLists;
      if (currentSmartListsRef !== lastSmartListsRef) {
        Object.keys(lastRenderedSmartListState).forEach((k) => delete lastRenderedSmartListState[k]);
        lastSmartListsRef = currentSmartListsRef;
      }

      const context: TemplateContext = {
        shop: { ...UserState.activeShop },
      };

      const widgets: HTMLElement[] = getTemplateWidgets();
      widgets
        .filter((widget: HTMLElement) => (
          widget.getAttribute('data-widgettype') === 'spm_smart_products_list'
          || widget.getAttribute('data-widgettype') === 'spm_fb_productslist'
          || (widget.getAttribute('data-widgettype') === 'spm_widget_import_zip' && htmlHasSmartlist(widget.innerHTML))
        ) && (!widgetToRefresh || widgetToRefresh === widget.getAttribute('id') || getFirstParent(widget, `#${widgetToRefresh}`)))
        .forEach((widget: HTMLElement) => {
          // We check if the original code of the widget is in the state otherwise we add it
          const widgetId = widget.getAttribute('id') ?? '';
          addSmartListOriginalCode(widgetId);
        });

      // Skip refresh if state hasn't changed since last render.
      // Cache key is computed AFTER addSmartListOriginalCode so it reflects the latest state
      // (on initial load, state is populated above before we check).
      const cacheKey = buildSmartListCacheKey(widgetToRefresh, effectiveLang);
      if (cacheKey && lastRenderedSmartListState[refreshSmartProductListTimeoutKey] === cacheKey) {
        if (widgetToRefresh) {
          delete refreshSmartProductListPending[widgetToRefresh];
        }
        return;
      }

      // We restore all smart lists in the template
      restoreSmartListsOriginalCode(widgetToRefresh);
      const template = getTemplateIframeDocument();

      // Re-apply translations for non-default language BEFORE rendering products.
      // restoreSmartListsOriginalCode puts back <spmtranslation> with default language text.
      // We update static text (title, button) to the target language before applySpmCollections
      // clones and renders the product loop elements.
      if (effectiveLang !== shopDefaultLanguage) {
        const translations = (TemplateEditorState.template?.translations ?? [])
          .filter((t) => t.language === effectiveLang);
        updateIframeTranslations(translations);
      }

      await applySpmCollections(template, context, effectiveLang, false);

      // Re-inject Outlook VML on smart-list buttons after the DOM has been rebuilt.
      // restoreSmartListsOriginalCode (above) overwrote the live DOM with the stored
      // template (VML-less) and applySpmCollections re-rendered the products from it,
      // so the conditional-comment VML that applyButtonVml had inserted around each
      // <a> is gone. Nothing else in this flow re-applies it, so list buttons would
      // render unstyled in Outlook after any list refresh (e.g. layout change).
      // refreshButtonVml self-gates on email templates, is deferred (rAF + 100ms) and
      // detached (cannot break this flow), and is idempotent (strips existing VML
      // before re-injecting). Scoped to list buttons only, so standalone buttons are
      // left untouched. (`table.spm_product_list_element_button` also carries the
      // `spm_button` class that refreshButtonVml resolves via closest().)
      const vmlScope = widgetToRefresh ? `#${widgetToRefresh} ` : '';
      refreshButtonVml(`${vmlScope}table.spm_product_list_element_button th a`);

      // Update cache after successful render
      if (cacheKey) {
        lastRenderedSmartListState[refreshSmartProductListTimeoutKey] = cacheKey;
      }

      addWidgetsListeners();
      if (widgetToRefresh) {
        delete refreshSmartProductListPending[widgetToRefresh];

        if (store.getters['liveEditor/getSelectedStructure']) {
          // In case we refresh a specific widget, and structure panel is opened, we add the toolbar inside the widget
          const widgetElement = template.querySelector(`#${widgetToRefresh}`);
          if (widgetElement) {
            addItemElementDecorations(widgetElement as HTMLElement, TemplateStructureEnum.WIDGET);
          }
        }
      }
    }
  }, 500);
};

export const removeImage = (selector: string) => {
  const template = getTemplateIframeDocument();
  const container: HTMLElement|null = template?.querySelector(selector);
  if (container && container.parentElement) {
    container.parentElement.classList.add('spm_hide');
  }
};

export const showImage = (selector: string) => {
  const template = getTemplateIframeDocument();
  const container: HTMLElement|null = template?.querySelector(selector);
  if (container && container.parentElement) {
    container.parentElement.classList.remove('spm_hide');
  }
};

/**
 * Update the HTML code field after layout changes
 * @param widgetId - ID of the widget to update
 */
export const updateSmartListHtmlCode = async (widgetId: string) => {
  // Get the updated HTML code from the internal state
  const htmlCode = getSmartListHtmlCode('', 'content', widgetId);

  if (htmlCode && store.getters['liveEditor/getSelectedStructure']) {
    const selectedStructure = store.getters['liveEditor/getSelectedStructure'];
    const { parserValues } = selectedStructure;

    // The HTML code field is directly in the widget tab:
    // templateBuilder.configs.widget -> items -> templateBuilder.fields.htmlCode -> ''
    const widgetConfigTab = parserValues['templateBuilder.configs.widget'];

    if (widgetConfigTab && widgetConfigTab.items) {
      // Access the HTML code field directly
      const htmlCodeGroup = widgetConfigTab.items['templateBuilder.fields.htmlCode'];

      if (htmlCodeGroup) {
        // Since the field label is empty string, we access it with ''
        const htmlCodeField = htmlCodeGroup[''];

        if (htmlCodeField && htmlCodeField.properties && htmlCodeField.properties.length > 0) {
          // Update the HTML code property value
          htmlCodeField.properties[0].value = htmlCode;

          // Update the store with the new parser values to trigger reactivity
          store.dispatch('liveEditor/updateSelectedStructureParserValues', {
            properties: htmlCodeField.properties,
            tabItemLabel: 'templateBuilder.configs.widget',
            fieldGroupLabel: 'templateBuilder.fields.htmlCode',
            fieldLabel: '',
          });
        }
      }
    }
  }
};

/**
 * Open the design panel to access global form styles
 * @param anchorId - Optional anchor ID to scroll to specific accordion section
 */
export const toggleDesignPanel = async (anchorId?: string) => {
  if (anchorId) {
    store.commit('liveEditor/setTargetAnchor', anchorId);
  }
  await togglePanel('designPanel');
};

/**
 * Adjust teaser width when shape changes to circle (max 120px)
 */
export const adjustTeaserWidthForShape = () => {
  const template = getTemplateIframeDocument();
  if (!template) return;

  const spmBody = template.querySelector('#spm_body.spm_teaser') as HTMLElement;
  const spmContent = template.querySelector('#spm_body.spm_teaser #spm_content') as HTMLElement;
  if (!spmBody || !spmContent) return;

  const isCircle = spmBody.classList.contains('spm_shape_circle');
  if (isCircle) {
    // Get current width, default to 120 if not set or greater than 120
    let currentWidth = parseInt(spmContent.style.width, 10);
    if (!currentWidth || currentWidth > 120) {
      currentWidth = 120;
    }
    // Apply width
    spmContent.style.width = `${currentWidth}px`;
    spmContent.style.minWidth = `${currentWidth}px`;
    spmBody.style.width = `${currentWidth}px`;
    // Sync height with width for perfect circle
    spmContent.style.height = `${currentWidth}px`;
    spmContent.style.minHeight = `${currentWidth}px`;
    spmContent.style.maxHeight = `${currentWidth}px`;
  } else {
    // Reset height for rectangle
    spmContent.style.height = '';
    spmContent.style.minHeight = '';
    spmContent.style.maxHeight = '';
  }

  // Force recalculate parser values and refresh configuration panel
  store.commit('liveEditor/refreshConfigurationPanelKey');
  const selectedStructure = store.getters['liveEditor/getSelectedStructure'];
  if (selectedStructure) {
    store.dispatch('liveEditor/prepareStructureConfiguration', {
      type: selectedStructure.type,
      identifier: selectedStructure.identifier,
    });
  }
};
